import { get } from "lodash";
import normalize from "json-api-normalizer";
import { push } from "connected-react-router";

import {
  CANCEL_PURCHASE_SUCCESS,
  CANCEL_PURCHASE_FAILED,
  EXPIRE_PURCHASE_SUCCESS,
  EXPIRE_PURCHASE_FAILED,
  CREATE_PURCHASE_START,
  CREATE_PURCHASE_FAILED,
  CREATE_PURCHASE_SUCCESS,
  FETCH_PURCHASES_START,
  FETCH_PURCHASES_SUCCESS,
  FETCH_PURCHASES_FAILED,
  UPDATE_PURCHASE_START,
  UPDATE_PURCHASE_SUCCESS,
  UPDATE_PURCHASE_FAILED,
  RENEW_PURCHASE_TOGGLE_FAILED,
  RENEW_PURCHASE_TOGGLE_SUCCESS
} from "./types";
import { FETCH_SUCCESS, FETCH_FAILED, FETCH_START } from "../actions";
import PurchasesService from "../services/purchases.service";
import NotificationService from "../services/notification.service";
import EncodingFiltersService from "../services/encoding-filters.service";

export const fetchPurchases = params => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: FETCH_PURCHASES_START });

  try {
    const result = await PurchasesService.getAll(tenantSlug, {
      filter: { ...params }
    });

    const normalizedPurchases = normalize(result.data);
    dispatch({ type: FETCH_PURCHASES_SUCCESS, payload: normalizedPurchases });
  } catch (e) {
    dispatch({ type: FETCH_PURCHASES_FAILED });
    NotificationService.addError(`Failed fetching the purchases! ${NotificationService.handleMessage(e)}`);
  }
};

export const fetchPurchase = ({ id }) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: FETCH_PURCHASES_START });

  try {
    const result = await PurchasesService.get(id, tenantSlug);

    const normalizedPurchases = normalize(result.data);
    dispatch({ type: FETCH_PURCHASES_SUCCESS, payload: normalizedPurchases });
  } catch (e) {
    dispatch({ type: FETCH_PURCHASES_FAILED });
    NotificationService.addError(`Failed fetching the purchases! ${NotificationService.handleMessage(e)}`);
  }
};

export const fetchPagedPurchases = params => async (dispatch, getState) => {
  params.page = { number: params.page.number || 1, size: 15 };

  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: FETCH_START, payload: { page: params.page.number } });
  try {
    const result = await PurchasesService.getAll(tenantSlug, EncodingFiltersService.encode(params));

    const data = normalize(result.data, { endpoint: "purchases" });

    dispatch({ type: FETCH_SUCCESS, payload: { ...data, page: params.page.number } });
  } catch (e) {
    dispatch({ type: FETCH_FAILED, payload: get(e, "response.data", {}) });

    NotificationService.addError(`Could not fetch purchases. ${NotificationService.handleMessage(e)}`);
  }
};

export const assignSubscription = data => async (dispatch, getState) => {
  const { productSlug, ownerSlug, assigneeSlug, productPriceId } = data;
  const remappedFields = {
    product: productSlug,
    owner: ownerSlug,
    assignee: assigneeSlug,
    productPrice: productPriceId
  };
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: CREATE_PURCHASE_START });

  try {
    await PurchasesService.create(tenantSlug, { ...data, ...remappedFields });
    dispatch({ type: CREATE_PURCHASE_SUCCESS });
    NotificationService.addSuccess("Subscription succesfully assigned!");
  } catch (e) {
    dispatch({ type: CREATE_PURCHASE_FAILED });
    NotificationService.addError(`Failed assigning the subscription! ${NotificationService.handleMessage(e)}`);
  }
};

export const createPurchase = data => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: CREATE_PURCHASE_START });

  try {
    await PurchasesService.create(tenantSlug, data);
    dispatch({ type: CREATE_PURCHASE_SUCCESS });
    NotificationService.addSuccess("Purchase was created");

    dispatch(push("/purchases"));
  } catch (e) {
    dispatch({ type: CREATE_PURCHASE_FAILED });
    NotificationService.addError(`Could not create the purchase : ${NotificationService.handleMessage(e)}`);
  }
};

export const updatePurchase = (id, data) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: UPDATE_PURCHASE_START });

  try {
    await PurchasesService.update(id, tenantSlug, data);
    dispatch({ type: UPDATE_PURCHASE_SUCCESS });
    NotificationService.addSuccess("Purchase was updated");
    dispatch(push("/purchases"));
  } catch (e) {
    dispatch({ type: UPDATE_PURCHASE_FAILED });
    NotificationService.addError(`Could not update the purchase : ${NotificationService.handleMessage(e)}`);
  }
};

export const cancelPurchase = data => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  try {
    await PurchasesService.cancelPurchase(tenantSlug, data);

    dispatch({ type: CANCEL_PURCHASE_SUCCESS });
    NotificationService.addSuccess("Cancel subscription succeeded!");
  } catch (e) {
    dispatch({ type: CANCEL_PURCHASE_FAILED });

    NotificationService.addError(`Failed canceling the subscription! ${NotificationService.handleMessage(e)}`);
  }
};

export const expirePurchase = data => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  try {
    await PurchasesService.expirePurchase(tenantSlug, data);

    dispatch({ type: EXPIRE_PURCHASE_SUCCESS });
    NotificationService.addSuccess("Expire purchase succeeded!");
  } catch (e) {
    dispatch({ type: EXPIRE_PURCHASE_FAILED });

    NotificationService.addError(`Failed expire the purchase! ${NotificationService.handleMessage(e)}`);
  }
};

export const generateExport = () => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  try {
    const result = await PurchasesService.generateExport(tenantSlug);

    const message = result.data.data.message;

    NotificationService.addSuccess(message);
  } catch (e) {
    NotificationService.addError(`Could not generate export: ${NotificationService.handleMessage(e)}`);
  }
};

export const renewPurchaseToggle = (id, fetchAction, page) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  try {
    await PurchasesService.renewPurchaseToggle(tenantSlug, id);

    dispatch({ type: RENEW_PURCHASE_TOGGLE_SUCCESS });
    NotificationService.addSuccess(`Purchase ${id} renewed successfully!`);
  } catch (e) {
    dispatch({ type: RENEW_PURCHASE_TOGGLE_FAILED });

    NotificationService.addError(`Failed renewing purchase ${id}! ${NotificationService.handleMessage(e)}`);
  }
};
